import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { X, CalendarClock, Car, CreditCard, User, Tag, CheckCircle2, AlertTriangle, Loader2, ArrowRight, ZoomIn } from 'lucide-react'
import { API_BASE, isApplePlatform, GOOGLE_WALLET_LIVE } from '../../lib/platform'
import { demoByEmail } from '../../lib/demo'
import { useAuth } from '../../store/authStore'
import { format } from 'date-fns'
import { cancelReservation } from '../../lib/api/booking'
import { formatCents } from '../../lib/money'
import { hhmm, prettyDayShort } from '../../lib/format'
import type { Locale, ReservationListItem } from '../../lib/api/types'
import { cn } from '../../lib/cn'


function statusStyle(status: string): string {
  const s = (status || '').toUpperCase()
  if (s.startsWith('CANCEL')) return 'bg-brand-100 text-brand-700'
  if (s === 'DRAFT' || s === 'PENDING') return 'bg-amber-100 text-amber-700'
  return 'bg-green-100 text-green-700'
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="flex items-center gap-2 text-sm text-ink-400">
        {icon}
        {label}
      </span>
      <span className="max-w-[62%] text-right text-sm font-semibold text-ink-900">{value}</span>
    </div>
  )
}

export function TicketModal({ r, onClose }: { r: ReservationListItem; onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const qc = useQueryClient()
  const user = useAuth((s) => s.user)
  const isDemo = !!demoByEmail(user?.email) // demo bookings aren't in the DB → use a real test code for the Wallet pass
  // Demo tickets have no DB record, so their code can't be signed. For testing (us + Apple review)
  // we route the demo Wallet button to a real internal booking (petteno) so the pass actually generates.
  const WALLET_TEST_CODE = 'P-0677929'
  const walletCode = isDemo ? WALLET_TEST_CODE : r.code

  const [phase, setPhase] = useState<'idle' | 'confirm' | 'busy' | 'done' | 'error'>('idle')
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [qrBig, setQrBig] = useState(false)

  // Backend rule: cancellable for CONFIRMED bookings up to the day before arrival (midnight).
  const todayStr = format(new Date(), 'yyyy-MM-dd')
  // Demo accounts map to a real internal booking (petteno) — never allow cancelling it from the demo.
  const cancellable = !isDemo && (r.status || '').toUpperCase() === 'CONFIRMED' && todayStr < r.arrivalDay

  async function doCancel() {
    setPhase('busy')
    setErrMsg(null)
    try {
      const res = await cancelReservation(r.code, r.cancellationCode)
      if (res.cancelled) {
        setPhase('done')
        qc.invalidateQueries({ queryKey: ['reservationList'] })
      } else {
        setPhase('error')
        setErrMsg(res.expired ? t('bookings.cancelExpired') : t('bookings.cancelError'))
      }
    } catch {
      setPhase('error')
      setErrMsg(t('bookings.cancelError'))
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (qrBig) setQrBig(false) // Esc closes the enlarged QR first, not the whole ticket
      else onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose, qrBig])

  const cust = r.customerData
  const services = (r.serviceData ?? []).filter((s) => s?.label)
  const statusLabel =
    (r.status || '').toUpperCase().startsWith('CANCEL')
      ? t('bookings.statusCancelled')
      : r.status === 'DRAFT'
        ? t('bookings.statusDraft')
        : t('bookings.statusConfirmed')

  return (
    // Outer = full-screen scroll container; the inner flex (min-h-full) centers the ticket when it
    // fits and grows to its height when it doesn't, so the WHOLE ticket scrolls and never clips.
    <div className="fixed inset-0 z-[80] overflow-y-auto bg-ink-950/50 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="w-full max-w-sm animate-fade-up"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          {/* ticket */}
          <div className="relative overflow-hidden rounded-3xl bg-white shadow-pop">
            {/* header */}
            <div className="flex items-center justify-between bg-brand-600 px-6 py-4 text-white">
              <div className="leading-tight">
                <p className="font-display text-[15px] font-extrabold tracking-tight">{t('brand.name')}</p>
                <p className="text-[10px] text-white/70">{t('brand.tagline')}</p>
              </div>
              <button onClick={onClose} className="rounded-lg p-1.5 text-white/90 transition hover:bg-white/15" aria-label={t('bookings.close')}>
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* QR — tap to enlarge */}
            <div className="flex flex-col items-center px-6 pb-2 pt-6">
              {/* status pill only when it carries info (cancelled / draft) — hidden for confermata to save height */}
              {(r.status || '').toUpperCase() !== 'CONFIRMED' && (
                <span className={cn('mb-4 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide', statusStyle(r.status))}>
                  {statusLabel}
                </span>
              )}
              <button
                type="button"
                onClick={() => setQrBig(true)}
                aria-label={t('bookings.qrHint')}
                className="group relative rounded-2xl border border-ink-100 bg-white p-3 shadow-card transition hover:shadow-card-hover"
              >
                <QRCodeSVG value={r.code} size={168} level="M" marginSize={0} />
                <span className="absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink-900/85 text-white shadow-sm transition group-hover:bg-ink-900">
                  <ZoomIn className="h-3.5 w-3.5" />
                </span>
              </button>
              <p className="mt-3 font-mono text-lg font-bold tracking-wide text-ink-900">{r.code}</p>
              <p className="mt-1 text-center text-xs text-ink-400">{t('bookings.qrHint')}</p>
              {/* Add to wallet — one OFFICIAL badge per platform: Apple on Apple devices, Google
                  elsewhere (Android/desktop). Localized: IT badge on /it, EN otherwise. */}
              {(r.status || '').toUpperCase() === 'CONFIRMED' && (
                <div className="mt-4 flex justify-center">
                  {isApplePlatform() ? (
                    <a
                      href={`${API_BASE}/api/wallet/apple/${encodeURIComponent(walletCode)}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t('bookings.appleWallet')}
                    >
                      <img src={`/badges/apple-wallet-${locale === 'it' ? 'it' : 'en'}.svg`} alt={t('bookings.appleWallet')} className="h-12 w-auto" />
                    </a>
                  ) : GOOGLE_WALLET_LIVE ? (
                    <a
                      href={`${API_BASE}/api/wallet/google/${encodeURIComponent(walletCode)}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t('bookings.googleWallet')}
                    >
                      <img src={`/badges/google-wallet-${locale === 'it' ? 'it' : 'en'}.svg`} alt={t('bookings.googleWallet')} className="h-12 w-auto" />
                    </a>
                  ) : null}
                </div>
              )}
            </div>

            {/* perforation */}
            <div className="relative my-3">
              <div className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-ink-950/50" />
              <div className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-ink-950/50" />
              <div className="mx-6 border-t-2 border-dashed border-ink-200" />
            </div>

            {/* details */}
            <div className="divide-y divide-ink-100 px-6 pb-2">
              <Row icon={<User className="h-4 w-4" />} label={t('summary.customer')} value={`${cust?.name ?? ''} ${cust?.lastname ?? ''}`.trim() || '—'} />
              <Row
                icon={<Car className="h-4 w-4" />}
                label={t('summary.vehicle')}
                value={
                  <span>
                    {r.transportData?.label ?? '—'}
                    {cust?.licensePlate ? (
                      <span className="ml-2 rounded-md border border-ink-200 bg-ink-50 px-1.5 py-0.5 font-mono text-xs tracking-wider">
                        {cust.licensePlate}
                      </span>
                    ) : null}
                  </span>
                }
              />
              {/* arrival → departure on one compact row: icon + "Sosta" label + arrow */}
              <div className="flex items-center justify-between gap-3 py-2.5">
                <span className="flex flex-none items-center gap-2 text-sm text-ink-400">
                  <CalendarClock className="h-4 w-4 flex-none" />
                  {t('summary.stay')}
                </span>
                <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-right text-sm font-semibold tabular-nums text-ink-900">
                  <span>
                    {prettyDayShort(r.arrivalDay, locale)} · {hhmm(r.arrivalTime)}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 flex-none text-ink-300" />
                  <span>
                    {prettyDayShort(r.departureDay, locale)} · {hhmm(r.departureTime)}
                  </span>
                </span>
              </div>
              {r.conventionData?.name && (
                <Row icon={<Tag className="h-4 w-4" />} label={t('services.convention')} value={r.conventionData.name} />
              )}
              {services.length > 0 && (
                <Row icon={<Tag className="h-4 w-4" />} label={t('price.services')} value={services.map((s) => s.label).join(', ')} />
              )}
              {r.paymentModeData?.label && (
                <Row icon={<CreditCard className="h-4 w-4" />} label={t('payment.method')} value={r.paymentModeData.label} />
              )}
            </div>

            {/* total */}
            <div className="flex items-center justify-between border-t border-ink-100 bg-ink-50 px-6 py-4">
              <span className="font-display text-sm font-bold text-ink-900">{t('price.total')}</span>
              <span className="font-display text-2xl font-extrabold text-ink-900">{formatCents(r.totalPrice, locale)}</span>
            </div>

            {/* cancellation */}
            {phase === 'done' ? (
              <div className="flex flex-col items-center gap-2 border-t border-ink-100 px-6 py-5 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-green-100 text-green-700">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <p className="text-sm font-semibold text-green-700">{t('bookings.cancelled')}</p>
                <button onClick={onClose} className="mt-1 text-sm font-semibold text-ink-500 hover:text-ink-800">
                  {t('common.back')}
                </button>
              </div>
            ) : cancellable ? (
              <div className="border-t border-ink-100 px-6 py-4">
                {phase === 'idle' ? (
                  <button
                    onClick={() => setPhase('confirm')}
                    className="w-full rounded-xl border border-brand-200 py-2.5 text-sm font-semibold text-brand-600 transition hover:bg-brand-50"
                  >
                    {t('bookings.cancel')}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-ink-800">{t('bookings.cancelConfirm')}</p>
                    <p className="text-xs text-ink-400">{t('bookings.refundNote')}</p>
                    {phase === 'error' && (
                      <p className="flex items-center gap-1.5 text-xs font-medium text-brand-600">
                        <AlertTriangle className="h-3.5 w-3.5 flex-none" />
                        {errMsg}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPhase('idle')}
                        disabled={phase === 'busy'}
                        className="flex-1 rounded-xl border border-ink-200 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50"
                      >
                        {t('bookings.keep')}
                      </button>
                      <button
                        onClick={doCancel}
                        disabled={phase === 'busy'}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-brand-600 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
                      >
                        {phase === 'busy' && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t('bookings.cancelYes')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* enlarged QR — its own layer above the ticket; tap / X / Esc closes only this, not the ticket */}
      {qrBig && (
        <div
          className="fixed inset-0 z-[90] flex flex-col items-center justify-center gap-5 bg-ink-950/90 p-6 backdrop-blur-sm"
          onClick={(e) => {
            e.stopPropagation()
            setQrBig(false)
          }}
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={(e) => {
              e.stopPropagation()
              setQrBig(false)
            }}
            className="absolute right-[calc(1rem_+_env(safe-area-inset-right))] top-[calc(1rem_+_env(safe-area-inset-top))] rounded-full bg-white/15 p-2 text-white transition hover:bg-white/25"
            aria-label={t('bookings.close')}
          >
            <X className="h-6 w-6" />
          </button>
          <div className="w-full max-w-[320px] rounded-3xl bg-white p-5 shadow-pop" onClick={(e) => e.stopPropagation()}>
            <QRCodeSVG value={r.code} size={320} level="M" marginSize={0} className="h-auto w-full" />
          </div>
          <p className="font-mono text-xl font-bold tracking-wide text-white">{r.code}</p>
          <p className="max-w-xs text-center text-sm text-white/70">{t('bookings.qrHint')}</p>
        </div>
      )}
    </div>
  )
}
